/**
 * Centralized Cheat Detection Configuration Service
 * Manages cheat detection settings and provides utilities for exam security
 */

class CheatDetectionConfig {
  constructor() {
    this.defaultSettings = {
      enableTabSwitchDetection: true,
      enableCopyPasteDetection: true,
      enableFullscreenDetection: true,
      enableWebcamDetection: true,
      enableKeyboardDetection: true,
      enableContextMenuDetection: true,
      enableSidePanelDetection: true,
      enableFocusLossDetection: true
    };
  }

  /**
   * Get cheat detection settings from exam data
   * @param {Object} examData - The exam data from database
   * @returns {Object} Cheat detection settings
   */
  getSettingsFromExam(examData) {
    if (!examData || !examData.cheatDetectionSettings) {
      console.log('🔧 No cheat detection settings found, using defaults');
      return { ...this.defaultSettings };
    }
    
    console.log('🔧 Found cheat detection settings:', examData.cheatDetectionSettings);
    
    // Merge with defaults to ensure all settings are present
    const mergedSettings = {
      ...this.defaultSettings,
      ...examData.cheatDetectionSettings
    };
    
    console.log('🔧 Merged settings:', mergedSettings);
    return mergedSettings;
  }

  /**
   * Check if a specific cheat detection feature is enabled
   * @param {Object} settings - Cheat detection settings
   * @param {string} feature - Feature name (e.g., 'enableTabSwitchDetection')
   * @returns {boolean} Whether the feature is enabled
   */
  isFeatureEnabled(settings, feature) {
    if (!settings || typeof settings[feature] !== 'boolean') {
      console.log(`🔧 Feature ${feature} not found in settings, using default: ${this.defaultSettings[feature]}`);
      return this.defaultSettings[feature];
    }
    console.log(`🔧 Feature ${feature} enabled: ${settings[feature]}`);
    return settings[feature];
  }

  /**
   * Get security configuration for ExamSecurity initialization
   * @param {Object} settings - Cheat detection settings
   * @returns {Object} Security configuration object
   */
  getSecurityConfig(settings) {
    const config = {
      webcamRequired: this.isFeatureEnabled(settings, 'enableWebcamDetection'),
      fullscreenRequired: this.isFeatureEnabled(settings, 'enableFullscreenDetection'),
      enableTabSwitchDetection: this.isFeatureEnabled(settings, 'enableTabSwitchDetection'),
      enableCopyPasteDetection: this.isFeatureEnabled(settings, 'enableCopyPasteDetection'),
      enableKeyboardDetection: this.isFeatureEnabled(settings, 'enableKeyboardDetection'),
      enableContextMenuDetection: this.isFeatureEnabled(settings, 'enableContextMenuDetection'),
      enableSidePanelDetection: this.isFeatureEnabled(settings, 'enableSidePanelDetection'),
      enableFocusLossDetection: this.isFeatureEnabled(settings, 'enableFocusLossDetection')
    };

    return config;
  }

  /**
   * Get session storage keys for cheat counts based on enabled features
   * @param {Object} settings - Cheat detection settings
   * @returns {Object} Mapping of cheat types to session storage keys
   */
  getCheatCountKeys(settings) {
    const keyMapping = {
      copyPaste: 'count_copypaste',
      tabSwitch: 'count_tabchange',
      fullscreen: 'count_fullscreen',
      altKey: 'countalt',
      ctrlKey: 'countctrl',
      webcamOff: 'count_webcamoff',
      contextMenu: 'count_contextmenu',
      noPerson: 'count_noPerson',
      multiplePersons: 'count_multiplePersons',
      suspiciousObjects: 'count_suspiciousObjects',
      sidePanel: 'count_sidepanel',
      focusLoss: 'count_focusloss'
    };

    // Return all keys, but the security system will check if features are enabled
    return keyMapping;
  }

  /**
   * Get professional alert messages for enabled features
   * @param {Object} settings - Cheat detection settings
   * @returns {Object} Mapping of cheat types to alert messages
   */
  getAlertMessages(settings) {
    const allMessages = {
      copyPaste: "⚠️ Copy-paste detected. Please type your responses manually.",
      tabSwitch: "⚠️ Tab switching detected. Please remain on the exam page.",
      fullscreen: "⚠️ Fullscreen exit detected. Please return to fullscreen mode.",
      altKey: "⚠️ Alt/Option key detected. Please avoid function keys.",
      ctrlKey: "⚠️ Control/Command key detected. Please avoid keyboard shortcuts.",
      webcamOff: "⚠️ Camera access issue. Please check your webcam connection.",
      contextMenu: "⚠️ Right-click detected. Context menu is disabled during exams.",
      noPerson: "⚠️ No person detected. Please ensure you are visible in camera.",
      multiplePersons: "⚠️ Multiple persons detected. Please ensure only you are visible.",
      suspiciousObjects: "⚠️ Suspicious object detected. Please clear your workspace.",
      sidePanel: "⚠️ Browser side panel or split screen detected. Please close it and keep only the assessment on screen.",
      focusLoss: "⚠️ You have moved outside the assessment window. Please stay on the assessment at all times."
    };

    // Return all messages - the security system will check if features are enabled
    return allMessages;
  }

  /**
   * Validate cheat detection settings
   * @param {Object} settings - Cheat detection settings to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateSettings(settings) {
    const errors = {};
    
    if (!settings || typeof settings !== 'object') {
      errors.settings = 'Cheat detection settings must be an object';
      return { isValid: false, errors };
    }

    const validKeys = Object.keys(this.defaultSettings);
    const providedKeys = Object.keys(settings);

    // Check for invalid keys
    const invalidKeys = providedKeys.filter(key => !validKeys.includes(key));
    if (invalidKeys.length > 0) {
      errors.invalidKeys = `Invalid settings keys: ${invalidKeys.join(', ')}`;
    }

    // Check for invalid values
    validKeys.forEach(key => {
      if (settings.hasOwnProperty(key) && typeof settings[key] !== 'boolean') {
        errors[key] = `${key} must be a boolean value`;
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

// Export singleton instance
export default new CheatDetectionConfig();
